import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
} from '@mui/material';
import { useNotify } from '../context/NotifyContext';
import errorMessage from '../utils/errorMessage';
import useFieldErrors from '../utils/useFieldErrors';

const AddReferenceModal = ({ open, onClose, onSave, onSuccess, apiCall, title, label, showFullName = false, initialName }) => {
  const notify = useNotify();
  const fields = useFieldErrors();
  const [name, setName] = useState('');
  const [fullName, setFullName] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && initialName) {
      setName(initialName);
    }
  }, [open, initialName]);

  const handleClose = () => {
    setName('');
    setFullName('');
    setDescription('');
    fields.reset();
    onClose();
  };

  const handleSubmit = async () => {
    if (!fields.validate({ name: !name.trim() && `${label || 'Name'} is required` })) return;

    setLoading(true);
    try {
      const data = { name, description };
      if (showFullName) {
        data.full_name = fullName;
      }
      
      // Support both onSave (old style) and apiCall + onSuccess (new style)
      if (onSave) {
        await onSave(data);
        handleClose();
      } else if (apiCall) {
        const response = await apiCall(data);
        if (onSuccess) {
          onSuccess(response.data);
        }
        handleClose();
      }
    } catch (error) {
      console.error('Error saving:', error);
      notify.error(errorMessage(error, 'Error saving item'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
      // Increase z-index to appear above parent dialog (including nested modals)
      // Use a very high z-index to ensure it appears above all nested HorseModals
      sx={{ zIndex: 1400 }}
    >
      <DialogTitle>Add New {title}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <TextField
                  size="small"
            label={label || 'Name'}
            value={name}
            onChange={(e) => { setName(e.target.value); fields.clear('name'); }}
            {...fields.props('name')}
            required
            fullWidth
            autoFocus
          />
          {showFullName && (
            <TextField
                  size="small"
              label="Full Name (Optional)"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              fullWidth
            />
          )}
          {(title === 'Horse Type' || title === 'Location' || title === 'Breeding Method' || title === 'Pregnancy Status' || title === 'Registration Organization') && (
            <TextField
                  size="small"
              label="Description (Optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              rows={2}
              fullWidth
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? 'Adding...' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddReferenceModal;

